<template>
  <div class="flex flex-col w-full" :class="scroll ? 'h-full min-h-0' : ''">
    <slot name="header">
      <div v-if="title && (filter || $slots['header-left'])" class="mb-3">
        <h2 class="font-medium text-gray-700 dark:text-gray-200">
          {{ title }}
        </h2>
        <p v-if="info" class="text-sm text-gray-600 font-normal dark:text-gray-300">
          {{ info }}
        </p>
      </div>
      <div v-if="title || filter || reorder || $slots['header-left'] || $slots['header-right'] || $slots['controls']" class="md:flex items-center justify-between pb-2">
        <div class="py-1 md:py-0">
          <slot name="header-left">
            <div class="flex items-center">
              <div v-if="!_reorderActive && filter" class="w-full md:w-auto">
                <text-field v-model="query" leading="search" :placeholder="$t('general.filter')" class="w-full md:w-50" />
              </div>

              <div v-if="title && !filter">
                <h2 class="font-medium text-gray-700 dark:text-gray-200">
                  {{ title }}
                </h2>
                <p v-if="info" class="text-sm text-gray-600 font-normal line-clamp-2 dark:text-gray-300">
                  {{ info }}
                </p>
              </div>
            </div>
          </slot>
        </div>
        <div class="py-1 md:py-0">
          <slot name="header-right">
            <div class="flex items-center space-x-1">
              <template v-if="!_reorderActive">
                <o-tooltip v-if="reorder" :text="$t('general.reorder')">
                  <o-button icon="reorder" flat outline @click="handleReorderClick" />
                </o-tooltip>

                <slot name="controls" />
              </template>

              <template v-else>
                <o-button v-if="reorder" flat outline @click="handleReorderCancel">
                  {{ $t('components.elements.table.cancelReorder') }}
                </o-button>
                <o-button v-if="reorder" variant="primary" @click="handleReorderConfirm">
                  {{ $t('components.elements.table.saveOrderChanges') }}
                </o-button>
              </template>
            </div>
          </slot>
        </div>
      </div>
    </slot>
    <div :class="[containerClass, scroll ? 'h-full min-h-0' : '']" class="flex flex-col flex-grow flex-shrink w-full relative">
      <component :is="scroll ? 'o-scroll' : 'div'" class="flex-grow flex-shrink w-full" :class="scroll ? 'h-full min-h-0' : ''" :x-axis="true">
        <table
          v-if="ready"
          :id="tableId"
          :class="currentClass"
        >
          <slot
            v-if="!renderResponsive"
            :tbody-class="theadClass.thead"
            :tr-class="theadClass.tr"
            :th-class="theadClass.th"
            :data="normalizedHeaders"
            name="thead"
          >
            <thead
              v-if="showHeader"
              :class="theadClass.thead"
            >
              <tr :class="theadClass.tr">
                <th
                  v-if="_reorderActive"
                  :class="[theadClass.th, 'w-12']"
                />
                <th
                  v-if="select"
                  :class="[theadClass.th]"
                >
                  <input
                    ref="select-all"
                    type="checkbox"
                    :value="data.length === selected.length"
                    :checked.prop="data.length === selected.length"
                    class="o-checkbox appearance-none h-4 w-4 border border-gray-300 dark:border-gray-700 rounded  checked:bg-blue-600 checked:border-blue-600"
                    @click="selectAll"
                  >
                </th>
                <th
                  v-for="({ text, className, id, sortKey }, index) in normalizedHeaders"
                  :id="id"
                  :key="index"
                  :class="[theadClass.th, className]"
                >
                  <div
                    :class="[theadClass.content, {'cursor-pointer' : sortKey}]"
                    @click="sortKey ? sortData(sortKey) : null"
                  >
                    <p class="leading-none text-left">
                      {{ text || '-' }}
                    </p>
                    <p class="ml-1">
                      <o-icon
                        v-if="sortKey && sort.key === sortKey"
                        class="cursor-pointer"
                        :icon="sort.order === 'asc' ? 'sortAsc' : 'sortDesc'"
                        :size="12"
                      />
                      <o-icon
                        v-if="sortKey && sort.key !== sortKey"
                        class="cursor-pointer"
                        icon="sortUnset"
                        :size="12"
                      />
                    </p>
                  </div>
                </th>
              </tr>
            </thead>
          </slot>

          <slot
            :tbody-class="tbodyClass.tbody"
            :tr-class="tbodyClass.tr"
            :td-class="tbodyClass.td"
            :th-class="theadClass.th"
            :data="page"
            :headers="normalizedHeaders"
            :render-responsive="renderResponsive"
            :reorder-active="_reorderActive"
            name="fixedRow"
          />
          <slot
            :tbody-class="tbodyClass.tbody"
            :tr-class="tbodyClass.tr"
            :td-class="tbodyClass.td"
            :th-class="theadClass.th"
            :data="page"
            :headers="normalizedHeaders"
            :render-responsive="renderResponsive"
            name="tbody"
          >
            <draggable
              v-if="_reorderActive"
              v-model="rows"
              tag="tbody"
              handle=".reorder-handle"
              :item-key="draggableItemKey"
              :class="tbodyClass.tbody"
              :group="group"
              @change="(e) => $emit('change', e)"
            >
              <template #item="{ element: row, index: rowIndex }">
                <tr
                  :class="[(rowIndex % 2 === 0) && striped ? tbodyClass.stripe : tbodyClass.tr]"
                >
                  <td
                    :class="tbodyClass.td"
                    class="w-14"
                    scope="row"
                  >
                    <o-icon icon="reorderHandle" size="md" class="reorder-handle bg-gray-300 dark:bg-white/10 rounded-md flex items-center justify-center hover:bg-gray-400 transition-color duration-200 cursor-move" />
                  </td>
                  <td
                    v-if="select"
                    :class="tbodyClass.td"
                    class="w-14"
                  >
                    <div class="flex justify-center">
                      <input v-model="selected" :value="row" type="checkbox" class="o-checkbox appearance-none h-4 w-4 border border-gray-300 dark:border-gray-700 rounded mx-auto checked:bg-blue-600 checked:border-blue-600">
                    </div>
                  </td>
                  <slot
                    :row-index="rowIndex"
                    :tr-class="tbodyClass.tr"
                    :td-class="tbodyClass.td"
                    :row="row"
                    :toggle="() => toggle(rowIndex)"
                    name="row"
                  >
                    <slot
                      v-for="(text, columnIndex) in getRowColumns(row)"
                      :row-index="rowIndex"
                      :column-index="columnIndex"
                      :td-class="tbodyClass.td"
                      :text="text"
                      name="column"
                    >
                      <td
                        :key="`${rowIndex}-${columnIndex}`"
                        :class="tbodyClass.td"
                      >
                        {{ getCell(text) }}
                      </td>
                    </slot>
                  </slot>
                </tr>
              </template>
            </draggable>

            <tbody v-for="(row, rowIndex) in rows" v-else :key="'tbody-' + draggableItemKey(row)">
              <tr
                :class="[(rowIndex % 2 === 0) && striped ? tbodyClass.stripe : tbodyClass.tr]"
              >
                <td
                  v-if="select"
                  :class="tbodyClass.td"
                  class="w-14"
                >
                  <div class="flex justify-center">
                    <input v-model="selected" :value="row" type="checkbox" class="o-checkbox appearance-none h-4 w-4 border border-gray-300 dark:border-gray-700 rounded mx-auto checked:bg-blue-600 checked:border-blue-600">
                  </div>
                </td>
                <slot
                  :row-index="rowIndex"
                  :tr-class="tbodyClass.tr"
                  :td-class="tbodyClass.td"
                  :row="row"
                  :toggle="() => toggle(rowIndex)"
                  name="row"
                >
                  <slot
                    v-for="(text, columnIndex) in getRowColumns(row)"
                    :row-index="rowIndex"
                    :column-index="columnIndex"
                    :td-class="tbodyClass.td"
                    :text="text"
                    name="column"
                  >
                    <td
                      :key="`${rowIndex}-${columnIndex}`"
                      :class="tbodyClass.td"
                    >
                      {{ getCell(text) }}
                    </td>
                  </slot>
                </slot>
              </tr>

              <tr v-if="collapse" :key="'accordion-' + draggableItemKey(row)">
                <td colspan="100">
                  <o-collapse :ref="'collapse' + rowIndex" no-header>
                    <div class="bg-gray-100 dark:bg-gray-900 p-4">
                      <slot name="collapse" :row="row" />
                    </div>
                  </o-collapse>
                </td>
              </tr>
            </tbody>
          </slot>
          <slot
            :tfoot-class="tfootClass.tfoot"
            :tr-class="tfootClass.tr"
            :td-class="tfootClass.td"
            :data="normalizedFooterData"
            :headers="normalizedHeaders"
            :render-responsive="renderResponsive"
            name="tfoot"
          >
            <tfoot
              v-if="showFooter"
              :class="tfootClass.tfoot"
            >
              <tr :class="tfootClass.tr">
                <td
                  v-for="({ text, className, id }, index) in normalizedFooterData"
                  :id="id"
                  :key="index"
                  :class="[tfootClass.td, className]"
                >
                  {{ text }}
                </td>
              </tr>
            </tfoot>
          </slot>
        </table>
      </component>
      <div v-if="!_reorderActive && !items.length && !$slots['tbody']" class="text-center my-4 text-sm opacity-80">
        {{ data.loading ? $t('general.loading') : placeholder }}
      </div>
      <div v-if="!_reorderActive && pages > 1" class="py-2 border-t dark:border-gray-800 flex justify-between items-center flex-shrink-0 px-4">
        <div class="hidden md:block text-sm" v-html="$t('components.elements.table.showing', { from: counter.from, to: counter.to, total: counter.total })" />
        <div class="flex space-x-2">
          <o-button
            flat
            translucent
            :disabled="current === 1"
            class="px-1"
            size="sm"
            icon="chevronLeftDouble"
            @click="current = 1"
          />
          <o-button
            flat
            translucent
            :disabled="current === 1"
            class="px-1"
            size="sm"
            icon="chevronLeft"
            @click="current--"
          />

          <template v-for="item in pages" :key="item">
            <o-button
              v-if="current - 3 < item && item < current + 3"

              flat
              :translucent="current !== item"
              size="sm"
              @click="current = item"
            >
              {{ item }}
            </o-button>
          </template>

          <o-button
            flat
            translucent
            :disabled="current === pages"
            class="px-1"
            size="sm"
            icon="chevronRight"
            @click="current++"
          />
          <o-button
            flat
            translucent
            :disabled="current === pages"
            class="px-1"
            size="sm"
            icon="chevronRightDouble"
            @click="current = pages"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'OTable' }
</script>

<script setup lang="ts">
import Fuse from 'fuse.js'
import pick from 'lodash/pick'
import get from 'lodash/get'

import draggable from 'vuedraggable'

import {
  TablePager,
} from '~/_api/lib/pager'
import {
  TextField,
} from '@/components/fields'

const { $i18n } = useNuxtApp()
const instance = getCurrentInstance()

interface Props {
  modelValue?: any[]
  data?: any[] | Record<string, any>
  headers?: any[]
  footerData?: any[]
  hideHeader?: boolean
  showFooter?: boolean
  select?: boolean
  limit?: number
  striped?: boolean
  collapse?: boolean
  title?: string | null
  info?: string | null
  placeholder?: string
  tableId?: string | null
  containerClass?: string
  tableClass?: string | object | any[]
  theadClass?: Record<string, any>
  tbodyClass?: Record<string, any>
  tfootClass?: Record<string, any>
  paginationClass?: string
  filter?: boolean
  reorder?: boolean
  reorderActive?: boolean | null
  group?: string
  responsive?: boolean
  scroll?: boolean
  responsiveBreakpoint?: number
  filterKeys?: string[]
  sortKeys?: Record<string, any> | null
  itemKey?: string | ((row: any) => string | number) | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  data: () => [],
  headers: () => [],
  footerData: () => [],
  hideHeader: false,
  showFooter: false,
  select: false,
  limit: 100,
  striped: true,
  collapse: false,
  title: null,
  info: null,
  placeholder: undefined,
  tableId: null,
  containerClass: '',
  tableClass: 'w-full overflow-hidden table',
  theadClass: () => ({
    thead: 'border-b dark:border-gray-900',
    tr: 'border-blue-700',
    th: 'font-normal p-4 pb-3 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-white text-base sticky top-0',
    content: 'flex item-center',
  }),
  tbodyClass: () => ({
    tfoot: '',
    tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
    td: 'py-3 px-4 text-base',
    stripe: 'transition-all duration-200 bg-gray-50/50 dark:bg-white/[0.01]',
  }),
  tfootClass: () => ({
    tbody: '',
    tr: '',
    td: 'p-3',
  }),
  paginationClass: '',
  filter: false,
  reorder: false,
  reorderActive: null,
  group: 'o-table',
  responsive: false,
  scroll: false,
  responsiveBreakpoint: 768,
  filterKeys: () => [],
  sortKeys: null,
  itemKey: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void
  (e: 'update:data', value: any): void
  (e: 'update:reorderActive', value: boolean): void
  (e: 'sort', value: any): void
  (e: 'change', value: any): void
  (e: 'reorder-click'): void
  (e: 'reorder-cancel'): void
  (e: 'reorder-confirm'): void
}>()

const placeholder = computed(() => props.placeholder ?? $i18n.t('components.elements.table.noData'))

const ready = ref(!props.responsive)
const windowWidth = ref<number | null>(null)
const current = ref(1)
const selected = ref<any[]>([])
const indeterminate = ref(false)
const query = ref('')
const sort = ref<{ key: string | null; order: string | null }>({
  key: null,
  order: null,
})
const page = ref<any[]>([])
const undo = ref<any[] | null>(null)
const reorderActiveLocal = ref(false)

const _reorderActive = computed({
  get () {
    if (props.reorderActive !== null) {
      return props.reorderActive
    } else {
      return reorderActiveLocal.value
    }
  },
  set (data: boolean) {
    if (props.reorderActive !== null) {
      emit('update:reorderActive', data)
    } else {
      reorderActiveLocal.value = data
    }
  },
})

const isTablePagerData = computed(() => {
  return props.data instanceof TablePager
})

const items = computed(() => {
  const data = props.data as any

  if (isTablePagerData.value) {
    return data.items
  }

  return data
})

const sorted = computed(() => {
  const key = sort.value.key
  const order = sort.value.order

  const filterEnabled = props.filter
  const q = query.value

  let result = items.value

  if (filterEnabled && q) {
    const fuse = new Fuse(result, {
      keys: props.filterKeys,
    })

    const results = fuse.search(q)
    result = results.map(({ item }: any) => item)
  }

  const currencySymbol = (instance?.proxy as any)?.$auth?.info?.location?.currencySymbol || '$'

  if (key) {
    const sortedItems = [...result].sort((a: any, b: any) => {
      let aVal = get(a, key)
      let bVal = get(b, key)

      if (typeof aVal === 'string' && aVal.startsWith(currencySymbol)) {
        const regex = new RegExp('(^\\' + currencySymbol + '|,)', 'g')
        aVal = Number(aVal.replace(regex, ''))
      } else if (typeof aVal === 'string' && !isNaN(aVal as any)) {
        aVal = Number(aVal)
      }

      if (typeof bVal === 'string' && bVal.startsWith(currencySymbol)) {
        const regex = new RegExp('(^\\' + currencySymbol + '|,)', 'g')
        bVal = Number(bVal.replace(regex, ''))
      } else if (typeof bVal === 'string' && !isNaN(bVal as any)) {
        bVal = Number(bVal)
      }

      if (aVal < bVal) {
        return order !== 'desc' ? 1 : -1
      }
      if (aVal > bVal) {
        return order !== 'desc' ? -1 : 1
      }
      return 0
    })
    return sortedItems
  }

  return result
})

const rows = computed({
  get () {
    if (_reorderActive.value) {
      if (isTablePagerData.value) {
        return (props.data as any).items
      } else {
        return props.data
      }
    }

    return page.value
  },
  set (data: any) {
    if (_reorderActive.value) {
      if (isTablePagerData.value) {
        ;(props.data as any).items = data
      } else {
        emit('update:data', data)
      }
    } else {
      page.value = data
    }
  },
})

const draggableKeyCandidates = [
  'id',
  'idReservation',
  'idReservationCustomer',
  'idWaitlist',
  'idEmployee',
  'idCustomer',
  'idService',
  'idServiceCategory',
  'idProduct',
  'idProductCategory',
  'idOrder',
  'idPurchaseOrder',
  'idVoucher',
  'idPass',
  'idForm',
  'idSegment',
  'idRole',
  'idResourceType',
  'idAdminUser',
  'idLocation',
]

const draggableFallbackKeys = new WeakMap<object, string>()
let draggableFallbackCounter = 0

function draggableItemKey (row: any): string | number {
  if (typeof props.itemKey === 'function') {
    const key = props.itemKey(row)
    if (key !== undefined && key !== null && key !== '') {
      return key
    }
  }

  if (typeof props.itemKey === 'string' && props.itemKey) {
    const explicitKey = get(row, props.itemKey)
    if (explicitKey !== undefined && explicitKey !== null && explicitKey !== '') {
      return explicitKey
    }
  }

  if (row && typeof row === 'object') {
    for (const keyName of draggableKeyCandidates) {
      const candidate = row[keyName]
      if (candidate !== undefined && candidate !== null && candidate !== '') {
        return candidate
      }
    }

    const existing = draggableFallbackKeys.get(row)
    if (existing) {
      return existing
    }

    const generated = `row-${draggableFallbackCounter++}`
    draggableFallbackKeys.set(row, generated)
    return generated
  }

  return String(row)
}

const pages = computed(() => {
  return (props.data as any).pages || Math.ceil(sorted.value.length / props.limit) || 0
})

const renderResponsive = computed(() => {
  return props.responsive && windowWidth.value && windowWidth.value < props.responsiveBreakpoint
})

const normalizedHeaders = computed(() => {
  return props.headers.map((header: any) => {
    if (typeof header === 'string') {
      return { text: header }
    }
    return header
  })
})

const normalizedFooterData = computed(() => {
  return props.footerData.map((footer: any) => {
    if (typeof footer === 'string') {
      return { text: footer }
    }
    return footer
  })
})

const headersValues = computed(() => {
  return props.headers.filter((h: any) => h.value).map((h: any) => h.value)
})

const showHeader = computed(() => !props.hideHeader)

const currentClass = computed(() => {
  const classes: any[] = ['o-table']
  if (props.tableClass) {
    classes.push(props.tableClass)
  }
  return classes
})

const counter = computed(() => {
  const cur = current.value
  const lim = props.limit
  const total = (props.data as any).total || items.value.length

  const position = (cur * lim) - lim

  const from = position + 1
  const to = position + lim > total ? total : position + lim

  return { from, to, total }
})

function updatePage () {
  const p = sorted.value

  if (isTablePagerData.value) {
    page.value = p
  } else {
    const cur = (current.value - 1) * props.limit
    page.value = p.slice(cur, cur + props.limit)
  }
}

watch(current, (p) => {
  if (isTablePagerData.value) {
    ;(props.data as any).load(p)
  }
  updatePage()
})

watch(() => props.modelValue, (value) => {
  selected.value = value
})

watch(selected, (sel) => {
  indeterminate.value = !!sel.length
  emit('update:modelValue', sel)
})

watch(sorted, () => {
  updatePage()
}, { immediate: true })

watch(_reorderActive, (reorderActiveVal) => {
  if (reorderActiveVal) {
    undo.value = page.value
  }
})

watch(sort, (s) => {
  emit('sort', s)
}, { deep: true })

watch(() => props.limit, () => {
  updatePage()
})

onMounted(() => {
  if (props.responsive) {
    windowWidth.value = window.innerWidth
    ready.value = true
    window.addEventListener('resize', () => {
      windowWidth.value = window.innerWidth
    })
  }

  if (props.sortKeys) {
    sort.value.key = props.sortKeys.key
    sort.value.order = props.sortKeys.order
  }
})

function getProp (obj: any, prop: string) {
  return prop.split('.').reduce((r: any, e: string) => r[e], obj)
}

function getRowColumns (row: any) {
  if (!headersValues.value.length) {
    return row
  }
  return pick(row, headersValues.value)
}

function getCell (cell: any) {
  if (cell === null || cell === undefined) {
    return ''
  }

  if (typeof cell === 'string') {
    return cell
  }

  if (typeof cell === 'object') {
    return Object.values(cell)[0] || '-'
  }

  return '-'
}

function selectAll () {
  const itms = items.value

  if (itms.length === selected.value.length) {
    selected.value = []
  } else {
    selected.value = itms
  }
}

function sortData (key: string) {
  const cur = sort.value.key
  const order = sort.value.order

  sort.value = {
    key: cur === key && order === 'asc' ? null : key,
    order: cur === key && order === 'asc' ? null : order === 'desc' ? 'asc' : 'desc',
  }
}

function toggle (index: number) {
  const collapseRefs = instance?.proxy?.$refs['collapse' + index] as any

  if (collapseRefs && collapseRefs[0]) {
    collapseRefs[0].toggle()
  }
}

function handleReorderClick () {
  emit('reorder-click')
  _reorderActive.value = true
}

function handleReorderCancel () {
  emit('reorder-cancel')
  page.value = undo.value || []
  _reorderActive.value = false
}

function handleReorderConfirm () {
  emit('reorder-confirm')
  _reorderActive.value = false
}
</script>
